"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Drawer as DrawerPrimitive } from "vaul";

import type { ShortVideo } from "@/lib/youtube-shorts";
import { youtubeThumbnail } from "@/lib/youtube";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { useConsent } from "@/lib/consent-context";
import { useRovingFocus } from "@/lib/use-roving-focus";
import YouTubePlayer from "@/components/video/youtube-player";

const DESKTOP_QUERY = "(min-width: 768px)";

function subscribeDesktop(onChange: () => void) {
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getDesktopSnapshot() {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

function getDesktopServerSnapshot() {
  return false;
}

function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    getDesktopSnapshot,
    getDesktopServerSnapshot,
  );
}

function PlayIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-7 translate-x-[2px]"
      aria-hidden
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

const PREVIEW_LOOP_END_SECONDS = 6;
const POSTER_FADE_DELAY_MS = 1500;

function StoryCard({
  video,
  onOpen,
  isPreviewing,
  onPreviewIntent,
  buttonRef,
  onKeyDown,
  isActive,
}: {
  video: ShortVideo;
  onOpen: (id: string) => void;
  isPreviewing: boolean;
  onPreviewIntent: () => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  isActive: boolean;
}) {
  const id = video.id;
  const { consent } = useConsent();
  const showPreview = isPreviewing && consent.marketing;
  const [posterHidden, setPosterHidden] = useState(false);

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (active) setPosterHidden(false);
    });
    const t = showPreview
      ? setTimeout(() => {
          if (active) setPosterHidden(true);
        }, POSTER_FADE_DELAY_MS)
      : null;
    return () => {
      active = false;
      if (t) clearTimeout(t);
    };
  }, [showPreview]);

  const previewParams = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    controls: "0",
    modestbranding: "1",
    rel: "0",
    iv_load_policy: "3",
    playsinline: "1",
    disablekb: "1",
    fs: "0",
    cc_load_policy: "0",
    loop: "1",
    playlist: id,
    start: "0",
    end: String(PREVIEW_LOOP_END_SECONDS),
  }).toString();

  return (
    <div
      className="relative aspect-9/16 w-[234px] shrink-0 overflow-hidden rounded-[16px] bg-black lg:w-[273px] lg:rounded-[24px]"
      onMouseEnter={onPreviewIntent}
      onFocus={onPreviewIntent}
    >
      {showPreview && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?${previewParams}`}
          title={video.title || "Story preview"}
          tabIndex={-1}
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute left-0 top-[-20%] h-[140%] w-full"
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={youtubeThumbnail(id)}
        alt=""
        className={`pointer-events-none absolute left-0 top-[-20%] h-[140%] w-full object-cover transition-opacity duration-300 ${
          posterHidden ? "opacity-0" : "opacity-100"
        }`}
        loading="lazy"
        aria-hidden
      />
      <button
        ref={buttonRef}
        type="button"
        onClick={() => onOpen(id)}
        onKeyDown={onKeyDown}
        tabIndex={isActive ? 0 : -1}
        aria-label={video.title ? `Open ${video.title}` : "Open full video"}
        className="video-poster-btn group absolute inset-0 size-full cursor-pointer"
      >
        <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15" />
        <span className="absolute left-1/2 top-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-purple-05 opacity-0 transition-opacity group-hover:opacity-100 lg:size-16">
          <PlayIcon />
        </span>
      </button>
    </div>
  );
}

function VideoFrame({ videoId, inline }: { videoId: string; inline: boolean }) {
  return (
    <div className="absolute inset-0 size-full">
      <YouTubePlayer
        videoId={videoId}
        title="Patient story"
        inline={inline}
        autoStart
        autoFocus
        ratio="9:16"
      />
    </div>
  );
}

function VideoDialog({
  videoId,
  onClose,
}: {
  videoId: string;
  onClose: () => void;
}) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <>
        <DialogPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-60 bg-black/60 backdrop-blur-[2px]" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              tabIndex={-1}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement).focus();
              }}
              onCloseAutoFocus={(e) => e.preventDefault()}
              className="fixed top-1/2 left-1/2 z-70 flex aspect-9/16 h-[min(88vh,820px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-3xl bg-white p-3 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
              style={{ animation: "video-dialog-in 0.2s ease-out" }}
            >
              <DialogPrimitive.Title className="sr-only">
                Patient story
              </DialogPrimitive.Title>
              <DialogPrimitive.Close
                aria-label="Close"
                className="absolute right-4 top-4 z-20 flex size-9 items-center justify-center rounded-full bg-purple-03 text-white shadow-md transition-colors hover:bg-purple-04"
              >
                <X className="size-4" strokeWidth={2.5} />
              </DialogPrimitive.Close>
              <div className="relative aspect-9/16 w-full overflow-hidden rounded-2xl bg-black">
                <VideoFrame videoId={videoId} inline />
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
        <style jsx global>{`
          @keyframes video-dialog-in {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>
      </>
    );
  }

  return (
    <DrawerPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]" />
        <DrawerPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto flex h-[90dvh] max-w-md flex-col rounded-t-[24px] bg-white text-black shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.25)] outline-none">
          <DrawerPrimitive.Title className="sr-only">
            Patient story
          </DrawerPrimitive.Title>
          <DrawerPrimitive.Description className="sr-only">
            Watch the full patient story video.
          </DrawerPrimitive.Description>
          <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-black/15" />
          <div className="flex min-h-0 flex-1 flex-col p-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div className="relative mx-auto aspect-9/16 h-full max-w-full overflow-hidden rounded-2xl bg-black">
              {/* Mobile: drop playsinline so iOS hands off to its native player. */}
              <VideoFrame videoId={videoId} inline={false} />
            </div>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

export function RealStoriesCarousel({
  videos,
  title,
}: {
  videos: ShortVideo[];
  title?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const openerIndex = useRef<number | null>(null);

  // Centre the focused card so its neighbours stay partly in view, making it
  // obvious the carousel holds more cards.
  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };

  // Mouse navigation — scroll roughly a viewport; snap settles on a card.
  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  // One tab stop for the arrow pair; Left/Right move between them, Tab leaves.
  const { ref: arrowsRef, rovingProps: arrowsRovingProps } =
    useRovingFocus<HTMLDivElement>();

  const handleCardKey = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const total = buttonRefs.current.length;
    if (total === 0) return;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % total;
    else if (e.key === "ArrowLeft") next = (index - 1 + total) % total;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = total - 1;
    else return;
    e.preventDefault();
    setActiveIndex(next);
    // preventScroll so the browser's focus nudge doesn't fight the centring
    // and slam the card flush to the edge, hiding the next one.
    buttonRefs.current[next]?.focus({ preventScroll: true });
    revealCard(next);
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [openVideoId, setOpenVideoId] = useState<string | null>(null);

  useEffect(() => {
    if (hasEnteredView) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredView(true);
          setPreviewingId(
            (cur) => cur ?? (videos[activeIndex] ?? videos[0])?.id ?? null,
          );
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(scroller);
    return () => obs.disconnect();
  }, [hasEnteredView, videos, activeIndex]);

  // Hover/focus on a specific card overrides the preview while leaving the dot in place.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = videos.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
      setAtStart(scroller.scrollLeft <= 1);
      setAtEnd(scroller.scrollLeft >= maxScroll - 1);
      if (!hasEnteredView) return;
      const centred = videos[closest];
      if (centred) setPreviewingId(centred.id);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [videos, hasEnteredView]);

  const arrowClass =
    "flex size-11 items-center justify-center rounded-full border border-black/10 text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03 aria-disabled:pointer-events-none aria-disabled:opacity-30";

  return (
    <>
    
      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-[clamp(1.25rem,4vw,2.5rem)]">
        {title && (
          <h2
            className={`text-purple-05 flex-1 text-center text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] ${
              isScrollable ? "lg:text-left" : ""
            }`}
          >
            {title}
          </h2>
        )}
        {isScrollable && (
          <div
            ref={arrowsRef}
            {...arrowsRovingProps}
            className="hidden shrink-0 items-center gap-2 lg:flex"
          >
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(-1)}
              aria-disabled={atStart || undefined}
              aria-label="Previous stories"
              className={arrowClass}
            >
              <ChevronLeft className="size-5" strokeWidth={2} />
            </button>
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(1)}
              aria-disabled={atEnd || undefined}
              aria-label="Next stories"
              className={arrowClass}
            >
              <ChevronRight className="size-5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
      <div className="relative mt-[clamp(2rem,4vw,3rem)] lg:mx-auto lg:max-w-[1280px] lg:px-[clamp(1.25rem,4vw,2.5rem)]">
      <div
        ref={scrollerRef}
        tabIndex={-1}
        className="scrollbar-none flex snap-x snap-mandatory gap-[clamp(0.75rem,1.5vw,1.25rem)] overflow-x-auto px-[clamp(1.25rem,4vw,2.5rem)] lg:px-0 [-ms-overflow-style:none] focus:outline-none [&::-webkit-scrollbar]:hidden"
      >
        {videos.map((v, i) => (
          <div
            key={v.id}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className="snap-center shrink-0"
          >
            <StoryCard
              video={v}
              onOpen={(id) => {
                track(ANALYTICS_EVENTS.VIDEO_PLAYED, {
                  module: "Patient Stories",
                  video: v.title || id,
                });
                openerIndex.current = i;
                // Blur so the poster isn't focused inside the aria-hidden main.
                (document.activeElement as HTMLElement | null)?.blur();
                setOpenVideoId(id);
              }}
              isPreviewing={previewingId === v.id}
              onPreviewIntent={() => setPreviewingId(v.id)}
              buttonRef={(el) => {
                buttonRefs.current[i] = el;
              }}
              onKeyDown={(e) => handleCardKey(e, i)}
              isActive={i === activeIndex}
            />
          </div>
        ))}
      </div>
      </div>

      {isScrollable && videos.length > 1 && (
        <div className="mt-6 flex justify-center lg:hidden">
          <div className="flex items-center">
            {videos.map((v, i) => (
              <button
                key={v.id}
                type="button"
                tabIndex={-1}
                onClick={() => {
                  cardRefs.current[i]?.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest",
                  });
                }}
                aria-label={`Go to story ${i + 1}`}
                className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
              >
                <span
                  aria-hidden
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    activeIndex === i ? "w-8 bg-purple-03" : "w-2 bg-purple-01"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {openVideoId && (
        <VideoDialog
          videoId={openVideoId}
          onClose={() => {
            setOpenVideoId(null);
  
            const i = openerIndex.current;
            if (i != null) {
              requestAnimationFrame(() => buttonRefs.current[i]?.focus());
            }
          }}
        />
      )}
    </>
  );
}
